import { useDispensing, useSearchableTabs } from '@hook'
import { DropDownIcon, TriDot } from '@icons'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { Badge, DropdownBtn, Loader, Table2 } from '@ui'
import { capitalizeDrugName, getError, getIncludedData, showToast } from '@util'
import { Box, Text } from 'grommet'
import { Checkmark } from 'grommet-icons'
import moment from 'moment'
import { useMemo, useState } from 'react'

const tabOptions = [
  { key: '', label: 'All' },
  { key: 'dispensed', label: 'Dispensed' },
  { key: 'pending', label: 'Pending' }
]
export const UnitDose = () => {
  const { activeTab, searchParams, setActiveTab } = useSearchableTabs({ tabKey: 'udds' })
  const { openModal } = useModalStore()
  const [loading, setLoading] = useState(false)
  const query = searchParams.get('query') || ''
  const page = searchParams.get('page') || ''
  const limit = searchParams.get('limit') || ''
  const { data, include, isLoading, updateDispense, pagination } = useDispensing({
    dispensary_type: 'udds',
    limit,
    page,
    query: query?.toLowerCase(),
    status: activeTab || ''
  })

  const handleSearch = (e: string) => {
    setActiveTab(e, 'query')
  }

  const handleUpdate = async (id: string) => {
    setLoading(true)
    const payload = {
      data: {
        attributes: {
          status: 'dispensed'
        },
        type: 'dispensing'
      }
    }
    try {
      await updateDispense({ id, payload })
      showToast('Marked as Dispensed')
    } catch (error) {
      getError(error)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      id: 'patientId',
      header: 'Patient Id',
      cell: ({ row }: any) => {
        const { ref } = getIncludedData(include, 'patient', row.original.relationships?.patient?.data?.id)?.attributes
        return <Text size="14px">{ref}</Text>
      }
    },
    {
      id: 'patientName',
      header: 'Patient Name',
      cell: ({ row }: any) => {
        const { first_name, last_name } = getIncludedData(
          include,
          'patient',
          row.original.relationships?.patient?.data?.id
        ).attributes
        return (
          <Text className="capitalize" size="14px">
            {first_name} {last_name}
          </Text>
        )
      }
    },
    {
      id: 'ward',
      header: 'Ward',
      cell: ({ row }: any) => <Text size="small">{row.original.attributes?.dispensary_unit || '-'}</Text>
    },
    {
      id: 'medication',
      header: 'Medication',
      cell: ({ row }: any) => {
        const { drug_name } = getIncludedData(
          include,
          'product_inventory',
          row.original.relationships?.product_inventory?.data?.id
        ).attributes
        return <Text size="14px">{capitalizeDrugName(drug_name)}</Text>
      }
    },
    {
      id: 'quantity',
      header: 'Quantity Supplied',
      cell: ({ row }: any) => <Text size="14px">{row.original.attributes?.qty_dispensed}</Text>
    },
    {
      id: 'duration',
      header: 'Supply Duration',
      cell: ({ row }: any) => <Text size="14px">{row.original.attributes?.supply_duration}</Text>
    },
    {
      id: 'nextDosage',
      header: 'Next Dosage',
      cell: ({ row }: any) => (
        <Text size="14px">
          {moment.utc(row.original.attributes?.medication_details?.next_dosage_at).format('DD/MM/YYYY hh:mmA')}
        </Text>
      )
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Box direction="row" gap="xsmall">
          <Badge label={row.original.meta?.status} size="small" status={row.original.meta?.status} />
        </Box>
      )
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }: any) => {
        const {
          id,
          meta: { status }
        } = row.original
        return (
          <DropdownBtn
            dropdownLabel={<TriDot />}
            options={[
              {
                handleClick: (onClose: () => void) => {
                  openModal(DOCTOR_MODAL_TYPE.DISPENSE_DETAIL, { id })
                  onClose()
                },
                label: 'View details'
              },
              ...(status === 'pending'
                ? [
                    {
                      handleClick: (onClose: () => void) => {
                        openModal(DOCTOR_MODAL_TYPE.DESPENSE_AS_PRESCRIBE, { dispenseId: id })
                        onClose()
                      },
                      label: 'Edit Dose'
                    },
                    {
                      handleClick: (onClose: () => void) => {
                        handleUpdate(id)
                        onClose()
                      },
                      label: 'Mark as Dispensed'
                    }
                  ]
                : [])
            ]}
          />
        )
      }
    }
  ]
  const dropDown = useMemo(
    () => (
      <DropdownBtn
        dropdownLabel={
          <Box align="center" direction="row" gap="4px" justify="center">
            <Text size="small" style={{ textTransform: 'capitalize' }}>
              {tabOptions.find(t => t.key === activeTab)?.label}
            </Text>
            <DropDownIcon />
          </Box>
        }
        options={tabOptions?.map(t => ({
          activeBtn: activeTab === t.key,
          handleClick: (onClose: () => void) => {
            setActiveTab(t.key)
            onClose()
          },
          icon: activeTab === t.key ? <Checkmark size="small" /> : <></>,
          label: t.label
        }))}
        style={{
          border: `1px solid rgba(0, 0, 0, 0.33)`,
          borderRadius: '100px',
          minWidth: '123px',
          padding: '6px 12px'
        }}
      />
    ),
    [activeTab, tabOptions]
  )

  return (
    <div className="flex flex-col gap-5">
      {loading && <Loader />}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between flex-wrap">
        <div className="flex flex-wrap gap-3">{dropDown}</div>
        <input
          className="px-4 py-3 w-full sm:w-[300px] border border-gray-300 rounded-[25px] focus:outline-none focus:border-blue-500"
          onChange={e => handleSearch(e.target.value)}
          placeholder="Search by name, medication, ID..."
          type="text"
          value={query}
        />
      </div>
      <div className="min-w-0 overflow-x-auto">
        <Table2
          columns={columns}
          data={data || []}
          isLoading={isLoading}
          message="No unit dose prescriptions found"
          pageCount={pagination?.count || 0}
        />
      </div>
    </div>
  )
}
